import Vue from 'vue'
import VueRouter, {RawLocation, Route, RouteConfig} from 'vue-router'
import {ProgramPaths} from "@/enum/ProgramPaths";
import {Helper} from "@/plugins/helper";
import {NavigationGuardNext} from "vue-router/types/router";
import {store} from "@/store";
import app from "@/main";
import {ekranAcikMiTazeleyerek, ENGEL_TAZELEME_ALT_SINIR_MS, ozetBayatMi, TAZELEME_ALT_SINIR_MS} from "@/utils/lisansKisit";
import {kapananSayfaHedefi, kapananSayfadanCik, SayfaErisimi} from "@/utils/sayfaErisimi";
import { applyTheme } from '@/plugins/vuetify';
import {rotaErisilebilir, yeniYetkiModu} from "@/plugins/yetkiKontrol";
import {takipListeBaglaminiRotayaGoreDogrula} from "@/helpers/TakipListeGezintiHelper";
import {YETKI_GECISI_YOLU} from "@/components/yetki-gecisi/yetkiGecisi";
import { SENKRON_KAPI_TAZELEME_ARALIGI_MS, senkronRotasiYonlendirmesi } from '@/utils/senkronKapisi';

Vue.use(VueRouter)

const clearCache = (to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
    localStorage.removeItem('accessToken');
    sessionStorage.clear();
    next();
}

// DEV-2684: eksik `yetki` alani icin /ben tazelemesi bu sayfa yuklemesinde denendi mi (kullaniciIzinKontrol).
let yetkiAlaniTazelemesiDenendi = false;

/** DEV-2684 — rota kapısının sorduğu bilgiler; muhafız ve izleyiciler aynı kaynaktan (helper, store) okur. */
interface RotaErisimi extends SayfaErisimi {
    izinli(anahtar: string, eskiModAnahtari?: string): boolean;
    kullanici(): any;
}

const rotaErisimi: RotaErisimi = {
    ekranlarAcikMi: (urun) => helper.lisansEkranlariAcikMi(urun),
    lisansDurumu: (urun) => helper.lisansDurumu(urun),
    ozellikAcikMi: (anahtar) => helper.ozellikAcikMi(anahtar),
    izinli: (anahtar, eskiModAnahtari) => helper.isAuthorized(anahtar, eskiModAnahtari),
    kullanici: () => store.state.user,
};

/**
 * DEV-2684 — rota kapısının kararı; gezinti muhafızı ve yetki izleyicisi ortak kullanır. Geçerse null, geçmezse
 * gidilecek yer: önce ürün/özellik (DEV-2113; KISITLI dönem içeri alınır), sonra rota izni (erişim yok).
 */
function rotaKapisiHedefi(rota: Route, erisim: RotaErisimi): RawLocation | null {
    const urunHedefi = kapananSayfaHedefi(rota.path, rota.matched.map((kayit) => kayit.meta), erisim);
    if (urunHedefi !== null) {
        return urunHedefi;
    }
    // @ts-ignore
    const meta: { isAuthenticationRequired: boolean, izinKey: string | string[], eskiModAnahtari?: string, yalnizYonetici?: boolean, yeniModAnahtari?: string } = rota.meta;
    // DEV-3416: yetki gerekmeyen rotada izin listesi okunmaz.
    if (!meta.isAuthenticationRequired) {
        return null;
    }
    // DEV-2684 Model Y: yalnizYonetici rotalari rotaErisilebilir kapisindan gecer (yonetici;
    // yeni modda meta.yeniModAnahtari da acar). Digerleri Adim 5'teki gibi helper.isAuthorized(izinKey)
    // ile karar verir (yonetici bypass + rolsuz kullanicida cokme yok).
    // izinKey dizi ise TÜM izinler gerekli; tek string ise tek izin (geriye dönük uyumlu).
    // Dizi + eskiModAnahtari: eski modda dizinin karşılığı tek eski izindir, yalnız o sorulur.
    const izinliMi = (key: string) => erisim.izinli(key, meta.eskiModAnahtari);
    const diziIzinliMi = (anahtarlar: string[]) => meta.eskiModAnahtari && !yeniYetkiModu(erisim.kullanici())
        ? erisim.izinli(meta.eskiModAnahtari)
        : anahtarlar.every((key: string) => erisim.izinli(key));
    const erisebilir = meta.yalnizYonetici === true
        ? rotaErisilebilir(meta, erisim.kullanici())
        : (Array.isArray(meta.izinKey) ? diziIzinliMi(meta.izinKey) : izinliMi(meta.izinKey));
    return erisebilir ? null : ProgramPaths.erisimYok;
}

const kullaniciIzinKontrol = async (to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
    try {
        if (store.getters.accessToken) {
            let user = store.getters.userData;
            if (!user.id) {
                let tenantList = await store.dispatch("getTenantList");
                store.commit('setTenantId', tenantList[0].tenant_id)
                user = await app.$http.get("/api/v1/ben");
                store.commit('setUser', user);
            } else {
                if (store.state.tenantList.length === 0) {
                    await store.dispatch("getTenantList");
                }
                // Deploy oncesi acilmis oturumlarda sessionStorage'daki user'da
                // `lisans_durum` yoktur; yenileme/demo bari o sekmede hic gorunmez.
                // Bir kereye mahsus tazelenir (alan geldiginde -- null olsa bile --
                // kosul bir daha saglanmaz).
                if (user.lisans_durum === undefined) {
                    if (user.yetki === undefined) {
                        yetkiAlaniTazelemesiDenendi = true;
                    }
                    user = await app.$http.get("/api/v1/ben");
                    store.commit('setUser', user);
                } else {
                    // DEV-2113 — kural özeti yoksa (deploy öncesi açılmış oturum) ya da
                    // sonraki geçiş anı geçmişse /ben tazelenir: sayfa yenilense de eski
                    // özet kalmaz. En fazla beş dakikada bir (tarayıcı saati kaymış olabilir).
                    if (ozetBayatMi(user.ozellik_durumu)) {
                        try {
                            user = await store.dispatch('ozellikDurumunuTazele', {enAzAralikMs: TAZELEME_ALT_SINIR_MS});
                        } catch (e) {
                            // Tazeleme hatası oturumu kapatmaz: eldeki (bayat) özetle devam
                            // edilir; engellemeyi zaten sunucu yapar.
                        }
                    }
                    // Ayrı koşul (else-if değil): özet tazelemesi `yetki`yi getirmediyse tek deneme yine yapılır.
                    if (user.yetki === undefined && !yetkiAlaniTazelemesiDenendi) {
                        // DEV-2684: `yetki` (menu/yetki karari) eksikse oturum basina BIR deneme: alani
                        // dondurmeyen API'de her gezintide /ben istenmez; hata oturumu dusurmez.
                        yetkiAlaniTazelemesiDenendi = true;
                        try {
                            user = await app.$http.get("/api/v1/ben");
                            store.commit('setUser', user);
                        } catch (e) {
                            console.error(e);
                        }
                    }
                }
            }
            // DEV-2113 — geçiş anı geldiğinde sayfa geçişi olmasa da /ben tazelensin.
            store.dispatch('ozellikTazelemesiniKur');
            // DEV-2113 — ürün ekranları ya da sayfanın özelliği kapalı görünüyorsa karar eldeki özetle
            // verilmeden önce özet tazelenir (lisans yenilenmiş, özellik açılmış olabilir).
            await ekranAcikMiTazeleyerek(
                () => kapananSayfaHedefi(to.path, to.matched.map((kayit) => kayit.meta), rotaErisimi) === null,
                () => store.dispatch('ozellikDurumunuTazele', {enAzAralikMs: ENGEL_TAZELEME_ALT_SINIR_MS})
            );
            const hedef = rotaKapisiHedefi(to, rotaErisimi);
            if (hedef === null) {
                // Otomatik sorgu kapısı (DEV-2603): sessionStorage'daki kullanıcı bayat
                // olabilir; e-İcraPro'ya girişte kapı değeri en çok yarım saatte bir
                // sunucudan tazelenir (`/ben` ağır: sayfa yenilemesi sıfır istek kalsın;
                // bu guard'da ya da girişte `/ben` az önce çekildiyse hiç istek gitmez).
                // Beklenmez (menü reaktif güncellenir) ve hata verse bile gezinmeyi etkilemez.
                if ("/" + to.path.split('/')[1] === ProgramPaths.eicrapro)
                    store.dispatch("senkronDurumunuTazele", {enAzAralikMs: SENKRON_KAPI_TAZELEME_ARALIGI_MS});
                next();
            } else
                next(hedef);
            //TODO: özel satınal ekranı olabilir
        } else {
            console.error('Token not found.');
            if (to.path != ProgramPaths.giris)
                next(ProgramPaths.giris);
        }
    } catch (e) {
        localStorage.removeItem('accessToken');
        sessionStorage.clear();
        console.error(e);
        Vue.prototype.$toast.error('Hata: ' + e.message);
        if (to.path != ProgramPaths.giris)
            next(ProgramPaths.giris);
    }
};

/**
 * Otomatik sorgu kapısı (DEV-2603, karar 20.10.2026): plan ekranı rotası. Önce
 * olağan kullanıcı/lisans kontrolü koşar; o bir yönlendirme isterse (giriş, erişim
 * yok) aynen uygulanır. Geçişe izin verdiyse kapı kontrol edilir: kapalıysa
 * e-İcraPro ana sayfasına yönlendirilir. Menü kalemi gizli olsa da adresi yazarak
 * ya da yer imiyle gelen kullanıcı buradan dönmelidir.
 */
const senkronRotasiKapisi = (to: Route, from: Route, next: NavigationGuardNext<Vue>) =>
    kullaniciIzinKontrol(to, from, async (...args: any[]) => {
        if (args.length > 0) {
            (next as any)(...args);
            return;
        }
        const yonlendirme = await senkronRotasiYonlendirmesi(store);
        if (yonlendirme) next(yonlendirme);
        else next();
    });

const routes: RouteConfig[] = [
    {
        path: '/',
        redirect: ProgramPaths.giris,
        meta: {
            isAuthenticationRequired: false,
        },
    },
    {
        path: '/giris',
        component: () => import('@/views/common/HomeView.vue'),
        beforeEnter: clearCache,
        meta: {
            isAuthenticationRequired: false,
        },
    },
    {
        path: '/giris-kontrol',
        component: () => import('@/views/common/LoginCallback.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro,
        component: () => import('@/views/common/YapimAsamasinda.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/buro-bilgileri',
        component: () => import('@/views/ofispro/BuroListeView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'buro-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/buro/:id',
        component: () => import('@/views/ofispro/BuroDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'buro-detay'
        }
    },
    {
        path: ProgramPaths.ofispro + '/personel/:id',
        component: () => import('@/views/ofispro/PersonelDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'buro-calisan-bilgisi-detay'
        }
    },
    {
        path: ProgramPaths.ofispro + '/vekaletler',
        component: () => import('@/views/ofispro/VekaletListeView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'vekalet-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/muvekkil-excel-aktar',
        component: () => import('@/views/ofispro/MuvekkilExcelAktarView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            // Ekran hem vekalet oluşturur hem müvekkil ekler → her iki yetki de şart (uçlar muvekkil-ekle ister).
            izinKey: ['vekalet-ekle', 'muvekkil-ekle']
        }
    },
    {
        path: ProgramPaths.ofispro + '/vekalet/:id',
        component: () => import('@/views/ofispro/VekaletDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'vekalet-detay'
        }
    },
    {
        path: ProgramPaths.ofispro + '/raporlar',
        component: () => import('@/views/ofispro/RaporListeView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'rapor-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/rapor/:id',
        component: () => import('@/views/ofispro/RaporDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'rapor-detay'
        }
    },
    {
        path: ProgramPaths.ofispro + '/dilekceler',
        component: () => import('@/views/ofispro/DilekceView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'dilekce-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/kasa',
        component: () => import('@/views/ofispro/KasaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'kasa-modulu'
        }
    },
    //TODO:ÇALIŞMADIĞI İÇİN KAPATILDI 1@sebo
    /*
    {
        path: ProgramPaths.ofispro + '/klasorler',
        component: () => import('@/views/ofispro/KlasorlerView.vue'),
        meta: {
            isAuthenticationRequired: true,
        }
    },*/
    {
        path: ProgramPaths.ofispro + '/ajanda',
        component: () => import('@/views/ofispro/AjandaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'ajanda-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/icw-aktar',
        component: () => import('@/views/ofispro/AktarView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'aktar-modulu'
        }
    },
    {
        path: ProgramPaths.ofispro + '/kullanici-izinleri',
        component: () => import('@/views/ofispro/OfisTercihlerView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        // DEV-2684: eski modda yalnız Sistem Yöneticisi (T5); yeni modda "Kullanıcı İzinleri"
        // (tercih-modulu) Görüntüle de açar (/ben anahtarı kullanici-izinleri).
        meta: {
            isAuthenticationRequired: true,
            yalnizYonetici: true,
            yeniModAnahtari: 'kullanici-izinleri'
        }
    },
    {
        // DEV-2684: eski moddaki büronun yeni yetki sistemine geçiş sayfası (geçiş bitince silinir).
        // Kullanıcı İzinleri rotasıyla aynı kapı: yalnız Sistem Yöneticisi (yeniModAnahtari yok: geçiş
        // yönetici işidir, yeni moddaki izin sahibine açılmaz).
        path: YETKI_GECISI_YOLU,
        component: () => import('@/views/ofispro/YetkiGecisiView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            yalnizYonetici: true
        }
    },
    {
        path: ProgramPaths.ofispro + '/hesap-bilgileri',
        component: () => import('@/views/ofispro/HesapBilgileriView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/muvekkil/:id',
        component: () => import('@/views/ofispro/MuvekkilDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'muvekkil-detay'
        }
    },
    {
        path: ProgramPaths.ofispro + '/islem-kaydi',
        component: () => import('@/views/ofispro/LogView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'islem-kaydi'
        }
    },
    {
        path: ProgramPaths.ofispro + '/hesaplamalar',
        name: 'ofispro-hesaplamalar',
        component: () => import('@/views/ofispro/Hesaplamalar.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'hesaplamalar',
            eskiModAnahtari: 'buro-modulu',
        }
    },
    {
        path: ProgramPaths.ofispro + '/dosya-raporlama-tipleri',
        component: () => import('@/views/ofispro/raporlama/DosyaRaporlamaTipleriView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/genel-istatistikler',
        component: () => import('@/views/ofispro/raporlama/GenelRaporlamaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'genel-istatistikler'
        }
    },{
        path: ProgramPaths.ofispro + '/tahsilatlar',
        component: () => import('@/views/ofispro/raporlama/TahsilatlarView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },{
        path: ProgramPaths.ofispro + '/karsilastirmali-tahsilatlar',
        component: () => import('@/views/ofispro/raporlama/KarsilastirmaliRaporlamaView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },{
        path: ProgramPaths.ofispro + '/takip-ve-tahsilat-raporu',
        component: () => import('@/views/ofispro/raporlama/TakipVeTahsilatRaporlamaView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/aktar-detay/:id',
        component: () => import('@/views/ofispro/AktarDetay.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/aktarilan-detay/:id',
        component: () => import('@/views/ofispro/AktarilanDosyaDetay.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/aktarilmayan-detay/:id',
        component: () => import('@/views/ofispro/AktarilmayanDosyaDetay.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ofispro + '/uyarilar',
        component: () => import('@/views/ofispro/OfisProUyarilar.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'uyarilar-modulu'
        }
    },
    {
        path: ProgramPaths.icrapro,
        component: () => import('@/views/common/YapimAsamasinda.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.icrapro + '/borclu/:id',
        component: () => import('@/views/icrapro/BorcluDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'borclu-detay'
        }
    },
    {
        path: ProgramPaths.icrapro + '/takip-alt-tip/:id',
        component: () => import('@/views/icrapro/TakipAltTipView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'takip-alt-tip-detay'
        }
    },
    {
        path: ProgramPaths.icrapro + '/takipler',
        component: () => import('@/views/icrapro/TakipListView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'takip-listesi'
        }
    },
    {
        path: ProgramPaths.icrapro + '/takip/:id',
        component: () => import('@/views/icrapro/TakipDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'takip-detay'
        }
    },
    {
        path: ProgramPaths.icrapro + '/takip-ekle',
        component: () => import('@/views/icrapro/TakipEkleView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'takip-ac'
        }
    },
    {
        path: ProgramPaths.icrapro + '/takip-ekle/:id',
        component: () => import('@/views/icrapro/TakipEkleView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'takip-detay'
        }
    },
    {
        path: ProgramPaths.icrapro + '/tercihler',
        component: () => import('@/views/icrapro/TercihlerView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'tercihler-modulu'
        }
    },
    {
        path: ProgramPaths.icrapro + '/toplu-isler',
        component: () => import('@/views/icrapro/TopluIslerView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'toplu-xml-indir'
        }
    },
    {
        path: ProgramPaths.icrapro + '/toplu-not',
        component: () => import('@/views/icrapro/TopluNotAlma.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'toplu-not'
        }
    },

    {
        path: ProgramPaths.icrapro + '/toplu-excel',
        component: () => import('@/views/icrapro/TopluExcelTakip.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'toplu-excel-takip'
        }
    },
    {
        path: ProgramPaths.icrapro + '/toplu-takip-tarihi',
        component: () => import('@/views/icrapro/TopluTakipTarihi.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'toplu-takip-tarihi'
        }
    },
    {
        path: ProgramPaths.icrapro + '/toplu-takip-durumu',
        component: () => import('@/views/icrapro/TopluTakipDurumu.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'toplu-takip-durumu'
        }
    },
    {
        path: ProgramPaths.icrapro + '/toplu-is',
        component: () => import('@/views/icrapro/TopluIsAtama.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
            izinKey: 'toplu-is'
        }
    },
    {
        path: ProgramPaths.icrapro + '/hesaplayici',
        component: () => import('@/views/icrapro/HesaplayiciView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'faiz-hesaplama-modulu'
        }
    },
    {
        path: ProgramPaths.icrapro + '/vekalet-ucreti-hesaplayici',
        component: () => import('@/views/icrapro/VekaletUcretiHesaplayiciView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: true,
            izinKey: 'faiz-hesaplama-modulu'
        }
    },
    {
        path: ProgramPaths.eicrapro,
        component: () => import('@/views/eicrapro/IslemlerView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/deneme-tahtasi',
        component: () => import('@/views/eicrapro/Servisler.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/evrak-gonder',
        component: () => import('@/views/eicrapro/EvrakGonderView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/harc-masraf-odeme',
        component: () => import('@/views/eicrapro/HarcMasrafOdeView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/kisi-kurum-sorgula',
        component: () => import('@/views/eicrapro/KisiKurumSorgulaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/senkron-plani',
        component: () => import('@/views/eicrapro/SenkronPlanView.vue'),
        beforeEnter: senkronRotasiKapisi,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/islemler',
        component: () => import('@/views/eicrapro/IslemlerView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-odeme',
        component: () => import('@/views/eicrapro/MtsOdemeView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-hazirla',
        component: () => import('@/views/eicrapro/TakipHazirlaBetaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-ac',
        component: () => import('@/views/eicrapro/TakipAcView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-takip-hazirla',
        component: () => import('@/views/eicrapro/MtsTakipHazirlaBetaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-takip-ac',
        component: () => import('@/views/eicrapro/MtsTakipAcView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-hash-list',
        component: () => import('@/views/eicrapro/HashListView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-tebligat-gonder',
        component: () => import('@/views/eicrapro/MtsTebligatView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-sorgula',
        component: () => import('@/views/eicrapro/TakipSorgulaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/vekil-sorgula',
        component: () => import('@/views/eicrapro/VekilSorgulaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/toplu-haciz-talebi-gonder',
        component: () => import('@/views/eicrapro/TopluHacizHazirlaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/toplu-tebligat-talebi-gonder',
        component: () => import('@/views/eicrapro/TopluTebligatHazirlaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/toplu-genel-talep-gonder',
        component: () => import('@/views/eicrapro/TopluGenelTalepHazirlaView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/hazir-evrak-gonder',
        component: () => import('@/views/eicrapro/HazirEvrakGonderView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/evrak-indir',
        component: () => import('@/views/eicrapro/EvrakIndirView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/makbuz-indir',
        component: () => import('@/views/eicrapro/MakbuzIndirView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-hazirla-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TakipHazirlaIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-ac-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TakipAcIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-takip-hazirla-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MtsTakipHazirlaIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-takip-hazirla-beta-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MtsTakipHazirlaBetaIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-takip-ac-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MtsTakipAcIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/takip-sorgula-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TakipSorgulaIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/evrak-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/EvrakGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/hazir-evrak-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/HazirEvrakGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/makbuz-indir-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MakbuzIndirIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/genel-talep-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TopluGenelTalepGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/tebligat-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TopluTebligatGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/haciz-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/TopluHacizGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/harc-masraf-ode-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/HarcMasrafOdeIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-ode-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MtsOdemeIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/mts-tebligat-gonder-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/MtsTebligatGonderIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.eicrapro + '/evrak-indir-detay/:id',
        component: () => import('@/views/eicrapro/islem-detay/EvrakIndirIslemDetayView.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.davapro,
        component: () => import('@/views/common/YapimAsamasinda.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.arabulucupro,
        component: () => import('@/views/common/YapimAsamasinda.vue'),
        beforeEnter: kullaniciIzinKontrol,
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.lisansDurumu,
        component: () => import('@/views/common/LisansDurumuView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: ProgramPaths.ozellikKapali,
        component: () => import('@/views/common/OzellikKapaliView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: '/erisim-yok',
        component: () => import('@/views/common/YasakView.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
    {
        path: '/:pathMatch(.*)*',
        component: () => import('@/views/common/NotFound.vue'),
        meta: {
            isAuthenticationRequired: false,
        }
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL || '/',
    routes
});

// DEV-2684 — sürmekte olan gezinti. Vue Router 3'te iptal edilen gezinti (next(false), açık sayfaya yinelenen
// gezinti, sayfa parçası yüklenemedi) afterEach'e uğramaz; her gezintinin geçtiği transitionTo tamamlanma ya da
// iptalle biter. Yalnız en son başlayan sayılır: öncekini router zaten iptal eder.
let surenGezinti: object | null = null;
// Gezinti sürerken kullanıcı tazelendi: açık sayfa gezinti bitince denetlenir.
let bekleyenDenetim = false;
const gecmis: any = (router as any).history;
const asilGecis = gecmis.transitionTo;
gecmis.transitionTo = (konum: RawLocation, tamam?: (rota: Route) => void, iptal?: (hata: any) => void) => {
    const gezinti = {};
    surenGezinti = gezinti;
    const bitti = () => {
        if (surenGezinti !== gezinti) {
            return;
        }
        surenGezinti = null;
        if (bekleyenDenetim) {
            Promise.resolve().then(acikSayfayiDenetle);
        }
    };
    try {
        return asilGecis.call(gecmis, konum,
            (rota: Route) => { bitti(); if (tamam) tamam(rota); },
            (hata: any) => { bitti(); if (iptal) iptal(hata); });
    } catch (e) {
        bitti();
        throw e;
    }
};

const helper = new Helper();
// DEV-2113 — sayfa açıkken lisans biterse ya da özellik büroya kapanırsa kullanıcı orada
// kalmaz: özet her tazelendiğinde açık sayfa muhafızla aynı kuralla yeniden denetlenir.
// İzleyici ilk geçiş bitince kurulur: store ile router birbirini içe aktarıyor, bu modül
// yüklenirken store henüz hazır olmayabilir (modül düzeyinde kurmak açılışta çökertirdi).
const kapananSayfaIzleyicisiniKur = () => {
    kapananSayfadanCik(router, store, rotaErisimi);
};
router.onReady(kapananSayfaIzleyicisiniKur, kapananSayfaIzleyicisiniKur);
// DEV-2684 — özet izleyicisinden sonra kurulur: ikisi aynı tazelemede tetiklenirse önce o yönlendirir,
// yetki izleyicisi gezinti sürdüğü için tekrar yönlendirmez.
router.onReady(yetkiIzleyicisiniKur, yetkiIzleyicisiniKur);

router.beforeEach(async (to, from, next) => {
    if (store.getters.accessToken && to.path == ProgramPaths.giris)
        next(ProgramPaths.ofispro);
    next();
});

router.afterEach((to, from) => {
    const themeKey = resolveRouteTheme(to);
    applyTheme(themeKey);
    // DEV-2857: kart listeden/karttan değil de başka yerden açıldıysa eski liste bağlamını bırak
    takipListeBaglaminiRotayaGoreDogrula(to, from);
});

/**
 * DEV-2684 — açık sayfadayken kullanıcı (yetki, lisans) tazelenirse sayfa gezintideki kapıyla yeniden
 * denetlenir; geçmiyorsa muhafızın göndereceği yere gidilir. Gezinti sürerken denetim gezinti bitene
 * (tamamlanma ya da iptal) ertelenir; çıkışta (kullanıcı boş) ve muhafızsız sayfada (erişim yok, lisans
 * ekranı) bakılmaz.
 */
function acikSayfayiDenetle() {
    if (surenGezinti !== null) {
        bekleyenDenetim = true;
        return;
    }
    bekleyenDenetim = false;
    const kullanici: any = store.state.user;
    if (!kullanici || !kullanici.id || !store.getters.accessToken) {
        return;
    }
    const rota = router.currentRoute;
    if (!rota.matched.some((kayit) => kayit.beforeEnter === kullaniciIzinKontrol || kayit.beforeEnter === senkronRotasiKapisi)) {
        return;
    }
    const hedef = rotaKapisiHedefi(rota, rotaErisimi);
    if (hedef !== null) {
        router.replace(hedef).catch(() => undefined);
    }
}

function yetkiIzleyicisiniKur() {
    store.watch((state: any) => state.user, acikSayfayiDenetle);
}

const PATH_THEME_MAP: Record<string, string> = {
    ofispro: 'ofispro',
    icrapro: 'icrapro',
    eicrapro: 'eicrapro',
};

function resolveRouteTheme(to: Route): string {
    const metaTheme = (to.meta && (to.meta as any).app) as string | undefined;
    if (typeof metaTheme === 'string' && metaTheme.length > 0) {
        return metaTheme;
    }

    const programKey = to.path.split('/').filter(Boolean)[0];
    if (programKey && PATH_THEME_MAP[programKey]) {
        return PATH_THEME_MAP[programKey];
    }

    return 'default';
}

export default router
